<template>
  <div class="page">
    <!-- 头部组件（Logo + 搜索 + 导航） -->
    <AppHeader />

    <!-- ===== 骨架屏 ===== -->
    <template v-if="loading">
      <!-- 头条 + 副标题骨架 -->
      <div class="skeleton skeleton--site-header">
        <div class="skeleton__bar skeleton__bar--headline"></div>
        <div class="skeleton__bar skeleton__bar--sub-title"></div>
      </div>

      <!-- Banner 区域骨架 -->
      <div class="skeleton skeleton--banner">
        <div class="skeleton__bar skeleton__bar--title"></div>
        <div class="skeleton__block skeleton__block--banner-img"></div>
        <div class="skeleton__bar skeleton__bar--meta"></div>
      </div>

      <!-- 主内容区骨架 -->
      <div class="main-content">
        <!-- 新闻列表骨架 -->
        <div class="skeleton skeleton--news-list">
          <div class="skeleton__block skeleton__block--featured"></div>
          <div class="skeleton__bar skeleton__bar--text"></div>
          <div class="skeleton__bar skeleton__bar--text"></div>
          <div class="skeleton__bar skeleton__bar--text"></div>
        </div>

        <!-- 市场监管骨架 -->
        <div class="skeleton skeleton--market">
          <div class="skeleton__bar skeleton__bar--section-title"></div>
          <div class="skeleton__bar skeleton__bar--text"></div>
          <div class="skeleton__bar skeleton__bar--text"></div>
          <div class="skeleton__bar skeleton__bar--text"></div>
        </div>

        <!-- 人物板块骨架 -->
        <div class="skeleton skeleton--people">
          <div class="skeleton__bar skeleton__bar--section-title"></div>
          <div class="skeleton__grid">
            <div class="skeleton__block skeleton__block--card"></div>
            <div class="skeleton__block skeleton__block--card"></div>
            <div class="skeleton__block skeleton__block--card"></div>
            <div class="skeleton__block skeleton__block--card"></div>
          </div>
        </div>

        <!-- 缩略图骨架 -->
        <div class="skeleton skeleton--thumb">
          <div class="skeleton__bar skeleton__bar--title"></div>
          <div class="skeleton__thumb-row">
            <div class="skeleton__block skeleton__block--thumb"></div>
            <div class="skeleton__block skeleton__block--thumb"></div>
            <div class="skeleton__block skeleton__block--thumb"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- ===== 实际内容 ===== -->
    <template v-else>
      <!-- 头条 + 副标题 -->
      <SiteHeader
        :headline="headline"
        :headline-link="headlineLink"
        :sub-title1="subTitle1"
        :sub-title1-link="subTitle1Link"
        :sub-title2="subTitle2"
      />

      <!-- Banner 轮播（标题+图片+来源时间） -->
      <BannerCarousel :items="bannerItems" />

      <!-- 主内容区 -->
      <div class="main-content">

        <!-- 新闻列表（第一条自动放到推荐阅读位置） -->
        <NewsList
          :items="newsItems"
          class="main-content__news-list"
        />

        <!-- 市场监管板块 -->
        <MarketSupervision
          title="市场监管"
          class="main-content__section"
        />

        <!-- 中间广告位 -->
        <BottomAd
          :image="midAdImage"
          :link="midAdLink"
          :show-tag="midAdShowTag"
          :fixed-bottom="false"
          class="main-content__feature-banner"
        />

        <!-- 人物板块 -->
        <PeopleSection
          title="人物"
          :items="peopleItems"
          :badge-bg="peopleBadgeBg"
          class="main-content__section"
        />

        <!-- 底部缩略图切换组件 -->
        <ThumbSlider
          :items="thumbSliderItems"
          class="main-content__thumb-slider"
        />
      </div>
    </template>

    <!-- 底部广告 -->
    <BottomAd
      :image="bottomAdImage"
      :link="bottomAdLink"
      :show-tag="bottomAdShowTag"
      :fixed-bottom="false"
      class="main-content__bottom-ad"
    />

   
  </div>
</template>

<script>
import AppHeader from './components/AppHeader.vue';
import SiteHeader from './components/SiteHeader.vue';
import BannerCarousel from './components/BannerCarousel.vue';
import NewsList from './components/NewsList.vue';
import MarketSupervision from './components/MarketSupervision.vue';
import PeopleSection from './components/PeopleSection.vue';
import ThumbSlider from './components/ThumbSlider.vue';
import BottomAd from './components/BottomAd.vue';
import { getNewsList } from '../../api/news';
import { getAds } from '../../api/ad';

/* 图片资源路径 */
const IMG = (name) => require(`./assets/img/${name}`);

export default {
  name: 'LanhuShouye',
  components: {
    AppHeader,
    SiteHeader,
    BannerCarousel,
    NewsList,
    MarketSupervision,
    PeopleSection,
    ThumbSlider,
    BottomAd
  },
  mounted() {
    this.loadAll();
  },
  data() {
    return {
      /* ── 头部数据（广告接口获取） ── */
      headline: '',
      headlineLink: '',
      subTitle1: '',
      subTitle1Link: '',
      subTitle2: '',

      /* ── 中间广告位数据（广告接口获取） ── */
      midAdImage: '',
      midAdLink: '',
      midAdShowTag: false,

      /* ── 底部广告位数据（广告接口获取） ── */
      bottomAdImage: '',
      bottomAdLink: '',
      bottomAdShowTag: false,

      /* ── 轮播数据（接口前3条） ── */
      bannerItems: [],

      /* ── 新闻列表数据（接口第4~7条） ── */
      newsItems: [],

      /* ── 人物板块数据（接口前4条） ── */
      peopleItems: [],
      peopleBadgeBg: IMG('形状结合@2x.png'),

      /* ── 底部缩略图数据（接口第5~7条） ── */
      thumbSliderItems: [],

      /* ── 加载状态 ── */
      loading: true
    };
  },
  methods: {
    async loadAll() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadHomeNews(),
          this.loadPeopleAndThumb(),
          this.loadAds()
        ]);
      } finally {
        this.loading = false;
      }
    },
    async loadAds() {
      const positions = [
        { key: 'm_index_main_title', handler: (d) => {
          this.headline = d.textContent || '';
          this.headlineLink = d.turnUrl || '';
        }},
        { key: 'm_index_sub_title', handler: (d) => {
          this.subTitle1 = d.textContent || '';
          this.subTitle1Link = d.turnUrl || '';
        }},
        { key: 'm_index_mid', handler: (d, isShow) => {
          this.midAdImage = d.imageUrl || '';
          this.midAdLink = d.turnUrl || '';
          this.midAdShowTag = isShow === 1;
        }},
        { key: 'm_index_btm', handler: (d, isShow) => {
          this.bottomAdImage = d.imageUrl || '';
          this.bottomAdLink = d.turnUrl || '';
          this.bottomAdShowTag = isShow === 1;
        }}
      ];
      const tasks = positions.map(async ({ key, handler }) => {
        try {
          const res = await getAds({ positionKey: key });
          const list = res.data || [];
          const adContent = Array.isArray(list) ? list[0] : (list.adContentList || [])[0];
          if (!adContent) return;
          handler(adContent, adContent.isShowTag);
        } catch (err) {
          console.error(`获取广告失败 (${key}):`, err);
        }
      });
      await Promise.all(tasks);
    },
    async loadHomeNews() {
      try {
        const res = await getNewsList({});
        const data = res.data;
        const list = Array.isArray(data) ? data : (data.records || data.list || data.rows || []);
        //this.bannerItems = list.slice(0, 3);
        this.newsItems = list.slice(3, 7);
      } catch (err) {
        console.error('获取首页新闻失败', err);
        this.bannerItems = [];
        this.newsItems = [];
      }
    },
    async loadPeopleAndThumb() {
      try {
        const res = await getNewsList({ categoryIdList: [16] });
        const data = res.data;
        const list = Array.isArray(data) ? data : (data.records || data.list || data.rows || []);
        this.peopleItems = list.slice(0, 4);
        this.thumbSliderItems = list.slice(4, 7);
      } catch (err) {
        console.error('获取人物板块新闻失败', err);
        this.peopleItems = [];
        this.thumbSliderItems = [];
      }
    }
  },
};
</script>

<style scoped>
/* ── 引入设计令牌 ── */
@import './assets/_variables.css';

/* ── 页面容器 ── */
.page {
  background-color: var(--color-white);
  position: relative;
  width: var(--page-width);
  height: auto;
  overflow: hidden;
}

/* ── 主内容区 ── */
.main-content {
  width: var(--page-width);
  height: auto;
  margin-bottom: 0;
}

.main-content__news-list {
  margin: 28px 0 0 var(--space-6);
}

.main-content__section {
  margin: 52px 0 0 var(--space-6);
}

.main-content__feature-banner {
  margin: 20px 0 0 var(--space-6);
}

.main-content__thumb-slider {
  margin: 40px 0 0 var(--space-6);
}

.main-content__bottom-ad {
  margin: 30px 0 40px var(--space-6);
}

/* ══════ 骨架屏 ══════ */
.skeleton {
  width: var(--content-width);
  margin-left: var(--space-6);
}

.skeleton--site-header {
  margin-top: 11px;
  padding: 20px var(--space-6) 16px;
  box-sizing: border-box;
}

.skeleton--banner {
  margin-top: 20px;
}

.skeleton--news-list {
  margin-top: 28px;
}

.skeleton--market,
.skeleton--people {
  margin-top: 52px;
}

.skeleton--thumb {
  margin-top: 40px;
}

/* 闪烁动画块基础样式 */
.skeleton__block,
.skeleton__bar {
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 37%,
    #f0f0f0 63%
  );
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
  border-radius: 4px;
}

@keyframes skeleton-shimmer {
  0%   { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* 骨架条 */
.skeleton__bar--headline {
  width: 80%;
  height: 40px;
  margin: 0 auto 14px;
}

.skeleton__bar--sub-title {
  width: 60%;
  height: 36px;
  margin: 0 auto;
}

.skeleton__bar--title {
  width: 100%;
  height: 30px;
  margin-bottom: 10px;
}

.skeleton__bar--meta {
  width: 200px;
  height: 18px;
  margin-top: 17px;
}

.skeleton__bar--text {
  width: 100%;
  height: 22px;
  margin-bottom: 20px;
}

.skeleton__bar--section-title {
  width: 120px;
  height: 30px;
  margin-bottom: 23px;
}

/* 骨架大块 */
.skeleton__block--banner-img {
  width: 100%;
  height: 425px;
  border-radius: var(--radius-sm);
}

.skeleton__block--featured {
  width: 100%;
  height: 120px;
  border-radius: var(--radius-lg);
  margin-bottom: 28px;
}

.skeleton__grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: var(--content-width);
}

.skeleton__block--card {
  width: 366px;
  height: 318px;
  border-radius: var(--radius-sm);
  margin-bottom: 23px;
}

.skeleton__thumb-row {
  display: flex;
  justify-content: space-between;
  width: var(--content-width);
  margin-top: 20px;
}

.skeleton__block--thumb {
  width: 234px;
  height: 134px;
  border-radius: var(--radius-sm);
}
</style>
